import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Button, Input, InputNumber, Select, Switch, Tag, Typography, Divider, notification, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, DragOutlined, InfoCircleOutlined, FontSizeOutlined,
  ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';

// Load Devanagari + Latin Google Fonts for the preview
if (typeof document !== 'undefined' && !document.getElementById('poster-designer-fonts')) {
  const link = document.createElement('link');
  link.id = 'poster-designer-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&family=Noto+Serif+Devanagari:wght@400;700&family=Baloo+2:wght@400;700&family=Hind:wght@400;700&family=Poppins:wght@400;700&family=Mukta:wght@400;800&family=Eczar:wght@400;700&family=Karma:wght@400;700&family=Biryani:wght@400;700&family=Vesper+Libre&family=Tillana:wght@400;700&family=Rajdhani:wght@400;700&family=Tiro+Devanagari+Hindi&family=Rozha+One&family=Yatra+One&family=Kalam:wght@400;700&family=Laila:wght@400;700&display=swap';
  document.head.appendChild(link);
}

const { Option } = Select;
const { Text, Title } = Typography;

const FONT_FAMILIES = [
  // ── Google Fonts (Devanagari) ──────────────────────────
  { label: 'Noto Sans Devanagari (Default)', value: 'Noto Sans Devanagari' },
  { label: 'Noto Serif Devanagari (Serif)',  value: 'Noto Serif Devanagari' },
  { label: 'Hind (Clean)',                   value: 'Hind' },
  { label: 'Mukta',                          value: 'Mukta' },
  { label: 'Mukta ExtraBold 800',            value: 'Mukta ExtraBold' },
  { label: 'Baloo 2 (Decorative)',           value: 'Baloo 2' },
  { label: 'Eczar',                          value: 'Eczar' },
  { label: 'Karma',                          value: 'Karma' },
  { label: 'Biryani',                        value: 'Biryani' },
  { label: 'Vesper Libre',                   value: 'Vesper Libre' },
  { label: 'Tillana',                        value: 'Tillana' },
  { label: 'Rajdhani',                       value: 'Rajdhani' },
  { label: 'Tiro Devanagari Hindi',          value: 'Tiro Devanagari Hindi' },
  { label: 'Rozha One',                      value: 'Rozha One' },
  { label: 'Yatra One',                      value: 'Yatra One' },
  { label: 'Kalam',                          value: 'Kalam' },
  { label: 'Laila',                          value: 'Laila' },
  // ── Windows System Fonts (Hindi) ──────────────────────
  { label: 'Kokila (Windows)',               value: 'Kokila' },
  { label: 'Aparajita (Windows)',            value: 'Aparajita' },
  { label: 'Mangal (Windows)',               value: 'Mangal' },
  { label: 'Utsaah (Windows)',               value: 'Utsaah' },
  { label: 'Sanskrit Text (Windows)',        value: 'Sanskrit Text' },
  { label: 'Gargi',                          value: 'Gargi' },
  { label: 'Lohit Devanagari',               value: 'Lohit Devanagari' },
  // ── General ───────────────────────────────────────────
  { label: 'Poppins (Modern)',               value: 'Poppins' },
  { label: 'Arial',                          value: 'Arial' },
  { label: 'Times New Roman',               value: 'Times New Roman' },
  { label: 'Georgia',                        value: 'Georgia' },
];

const DEFAULT_FONT = 'Noto Sans Devanagari';

const TOKENS = [
  { key: 'name',             label: 'नाम' },
  { key: 'relation',         label: 'रिश्ता' },
  { key: 'father_name',      label: 'पिता का नाम' },
  { key: 'village',          label: 'ग्राम' },
  { key: 'tehsil',           label: 'तहसील' },
  { key: 'district',         label: 'जिला' },
  { key: 'address',          label: 'पूरा पता' },
  { key: 'house_no',         label: 'मकान नं.' },
  { key: 'phone',            label: 'फोन' },
  { key: 'occupation',       label: 'व्यवसाय' },
  { key: 'occ_district',    label: 'व्यवसाय जिला' },
  { key: 'occ_state',       label: 'व्यवसाय राज्य' },
  { key: 'total_payment',    label: 'कुल भुगतान' },
  { key: 'last_payment',     label: 'अंतिम भुगतान' },
  { key: 'last_payment_date',label: 'भुगतान तारीख' },
  { key: 'receipt_no',       label: 'रसीद नं.' },
  { key: 'payment_start',    label: 'भुगतान शुरुआत' },
  { key: 'payment_end',      label: 'भुगतान समाप्त' },
  { key: 'payment_count',    label: 'भुगतान संख्या' },
  { key: 'birth_date',       label: 'जन्म तिथि (पूर्ण)' },
  { key: 'birth_day',        label: 'जन्म दिन' },
  { key: 'birth_month',      label: 'जन्म माह' },
  { key: 'birth_year',       label: 'जन्म वर्ष' },
  { key: 'date',             label: 'आज की तारीख' },
];

const genId = () => `f_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

const DEFAULT_PHOTO = { enabled: false, top_pct: 10, left_pct: 40, size_pct: 15, width_pct: 15, height_pct: 15, circle: true, border_radius: 0, border_enabled: false, border_width: 4, border_color: '#ffffff' };

function PosterDesigner({ posterImageUrl, posterType, onSaved }) {
  const [fields, setFields]         = useState([]);
  const [photo, setPhoto]           = useState(DEFAULT_PHOTO);
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging]     = useState(null);
  const [resizing, setResizing]     = useState(null); // { handle, startX, startY, origW, origH, origTop, origLeft }
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const containerRef                = useRef(null);

  // Load saved config on mount / when posterType changes
  useEffect(() => {
    if (!posterType) return;
    setLoading(true);
    axios.get(`${baseUrl}/poster/text-config/${posterType}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    }).then(res => {
      if (res.data.status && res.data.result) {
        const cfg = res.data.result;
        const rows = (cfg.rows || []).map(r => ({ ...r, id: r.id || genId() }));
        setFields(rows);
        if (cfg.photo) setPhoto({ ...DEFAULT_PHOTO, ...cfg.photo });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [posterType]);

  // ── Drag handlers — attached to window so fast mouse moves don't cancel drag ─
  const onMouseDown = useCallback((e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current) return;
    const isPhoto = id === '__photo__';
    const origTop  = isPhoto ? photo.top_pct  : (fields.find(f => f.id === id)?.top_pct  ?? 50);
    const origLeft = isPhoto ? photo.left_pct : (fields.find(f => f.id === id)?.left_pct ?? 50);
    setSelectedId(id);
    setDragging({ id, startX: e.clientX, startY: e.clientY, origTop, origLeft });
  }, [fields, photo]);

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragging.startX) / rect.width)  * 100;
      const dy = ((e.clientY - dragging.startY) / rect.height) * 100;
      const newTop  = Math.max(0, Math.min(99,  dragging.origTop  + dy));
      const newLeft = Math.max(0, Math.min(100, dragging.origLeft + dx));
      if (dragging.id === '__photo__') {
        setPhoto(p => ({ ...p, top_pct: newTop, left_pct: newLeft }));
      } else {
        setFields(prev => prev.map(f => f.id === dragging.id ? { ...f, top_pct: newTop, left_pct: newLeft } : f));
      }
    };

    const onUp = () => setDragging(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  // ── Resize handler (photo overlay only) ───────────────────────────────────
  const onResizeMouseDown = useCallback((e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current) return;
    setResizing({
      handle,
      startX: e.clientX,
      startY: e.clientY,
      origW:    photo.width_pct  ?? photo.size_pct ?? 15,
      origH:    photo.height_pct ?? photo.size_pct ?? 15,
      origTop:  photo.top_pct  ?? 10,
      origLeft: photo.left_pct ?? 40,
    });
  }, [photo]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - resizing.startX) / rect.width)  * 100;
      const dy = ((e.clientY - resizing.startY) / rect.height) * 100;
      const h  = resizing.handle;
      let newW    = resizing.origW;
      let newH    = resizing.origH;
      let newTop  = resizing.origTop;
      let newLeft = resizing.origLeft;
      // Horizontal resize
      if (h.includes('e'))  newW = Math.max(2, resizing.origW + dx);
      if (h.includes('w')) { newW = Math.max(2, resizing.origW - dx); newLeft = resizing.origLeft + dx; }
      // Vertical resize
      if (h.includes('s'))  newH = Math.max(2, resizing.origH + dy);
      if (h.includes('n')) { newH = Math.max(2, resizing.origH - dy); newTop  = resizing.origTop  + dy; }
      setPhoto(p => ({
        ...p,
        width_pct:  Math.min(100, +newW.toFixed(1)),
        height_pct: Math.min(100, +newH.toFixed(1)),
        top_pct:    Math.max(0, +newTop.toFixed(1)),
        left_pct:   Math.max(0, +newLeft.toFixed(1)),
      }));
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',  onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',  onUp);
    };
  }, [resizing]);

  // ── Field operations ───────────────────────────────────────────────────────
  const addField = () => {
    const f = { id: genId(), template: '{name}', top_pct: 50, left_pct: 50, font_size: 60, color: '#000000', bold: true, text_anchor: 'middle', font_family: DEFAULT_FONT };
    setFields(prev => [...prev, f]);
    setSelectedId(f.id);
  };

  const update = (id, key, val) => setFields(prev => prev.map(f => f.id === id ? { ...f, [key]: val } : f));

  const remove = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const insertToken = (token) => {
    if (!selectedId) return;
    update(selectedId, 'template', (fields.find(f => f.id === selectedId)?.template || '') + `{${token}}`);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      const config = { rows: fields, photo };
      const res = await axios.post(`${baseUrl}/poster/save-text-config`,
        { poster_type: posterType, text_config: config },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (res.data.status) {
        notification.success({ message: 'Layout saved!', placement: 'topRight' });
        if (onSaved) onSaved(config);
      } else {
        notification.error({ message: res.data.message, placement: 'topRight' });
      }
    } catch {
      notification.error({ message: 'Failed to save layout', placement: 'topRight' });
    } finally {
      setSaving(false);
    }
  };

  const sel = fields.find(f => f.id === selectedId);

  // Preview scale — poster is displayed at fixed width, font preview scaled down
  const PREVIEW_W = 340;

  return (
    <div style={{ marginTop: 16, border: '1px solid #e8e8e8', borderRadius: 8, padding: 16, background: '#fafafa' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          <DragOutlined style={{ marginRight: 6, color: '#1890ff' }} />
          Poster Layout Designer
        </Title>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
          Save Layout
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

        {/* ── Poster Canvas ── */}
        <div>
          <div
            ref={containerRef}
            style={{
              position: 'relative',
              width: PREVIEW_W,
              border: '2px solid #1890ff',
              borderRadius: 4,
              overflow: 'visible',
              cursor: resizing ? 'auto' : (dragging ? 'grabbing' : 'crosshair'),
              userSelect: 'none',
              background: '#e0e0e0',
            }}
            onClick={(e) => { if (e.target === containerRef.current || e.target.tagName === 'IMG') setSelectedId(null); }}
          >
            {/* Image wrapper clips the poster but not the overlay handles */}
            <div style={{ overflow: 'hidden', borderRadius: 4 }}>
              {posterImageUrl
                ? <img src={posterImageUrl} alt="Poster" style={{ width: '100%', display: 'block' }} draggable={false} />
                : <div style={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text type="secondary">Upload poster image first</Text>
                  </div>
              }
            </div>

            {/* Draggable text overlays */}
            {fields.map(f => (
              <div
                key={f.id}
                style={{
                  position: 'absolute',
                  top: `${f.top_pct}%`,
                  left: `${f.left_pct ?? 50}%`,
                  transform: (f.text_anchor || 'middle') === 'middle' ? 'translateX(-50%)'
                    : (f.text_anchor === 'end' ? 'translateX(-100%)' : 'none'),
                  cursor: dragging?.id === f.id ? 'grabbing' : 'grab',
                  padding: '1px 5px',
                  background: selectedId === f.id ? 'rgba(24,144,255,0.18)' : 'rgba(255,255,255,0.12)',
                  border: `1px dashed ${selectedId === f.id ? '#1890ff' : 'rgba(0,0,0,0.35)'}`,
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                  fontSize: Math.max(7, Math.min(Math.round(f.font_size * PREVIEW_W / 2400), 15)),
                  fontWeight: f.bold ? 'bold' : 'normal',
                  fontFamily: `'${f.font_family || DEFAULT_FONT}', Arial, sans-serif`,
                  color: f.color || '#000',
                  lineHeight: 1.5,
                  zIndex: selectedId === f.id ? 20 : 10,
                }}
                onMouseDown={(e) => onMouseDown(e, f.id)}
              >
                {f.template || '(empty)'}
              </div>
            ))}

            {/* Photo placeholder */}
            {photo.enabled && (() => {
              const isActive = selectedId === '__photo__';
              const handleStyle = (cursor) => ({
                position: 'absolute', width: 10, height: 10,
                background: '#fff', border: '2px solid #1890ff',
                borderRadius: 2, zIndex: 20, cursor,
              });
              return (
                <div
                  style={{
                    position: 'absolute',
                    top: `${photo.top_pct}%`,
                    left: `${photo.left_pct}%`,
                    width: `${photo.width_pct ?? photo.size_pct ?? 15}%`,
                    height: `${photo.height_pct ?? photo.size_pct ?? 15}%`,
                    border: `2px dashed ${isActive ? '#1890ff' : '#52c41a'}`,
                    borderRadius: photo.circle ? '50%' : `${photo.border_radius || 0}px`,
                    background: 'rgba(82,196,26,0.10)',
                    zIndex: 5,
                    cursor: resizing ? 'auto' : (dragging?.id === '__photo__' ? 'grabbing' : 'grab'),
                    boxSizing: 'border-box',
                  }}
                  onMouseDown={(e) => onMouseDown(e, '__photo__')}
                >
                  {/* Resize handles — shown always so user can resize without selecting first */}
                  <div style={{ ...handleStyle('nw-resize'), top: -5, left: -5 }}   onMouseDown={(e) => onResizeMouseDown(e, 'nw')} />
                  <div style={{ ...handleStyle('ne-resize'), top: -5, right: -5 }}  onMouseDown={(e) => onResizeMouseDown(e, 'ne')} />
                  <div style={{ ...handleStyle('sw-resize'), bottom: -5, left: -5 }} onMouseDown={(e) => onResizeMouseDown(e, 'sw')} />
                  <div style={{ ...handleStyle('se-resize'), bottom: -5, right: -5 }} onMouseDown={(e) => onResizeMouseDown(e, 'se')} />
                  <div style={{ ...handleStyle('e-resize'),  top: 'calc(50% - 5px)', right: -5 }}  onMouseDown={(e) => onResizeMouseDown(e, 'e')} />
                  <div style={{ ...handleStyle('w-resize'),  top: 'calc(50% - 5px)', left: -5 }}   onMouseDown={(e) => onResizeMouseDown(e, 'w')} />
                  <div style={{ ...handleStyle('s-resize'),  bottom: -5, left: 'calc(50% - 5px)' }} onMouseDown={(e) => onResizeMouseDown(e, 's')} />
                  <div style={{ ...handleStyle('n-resize'),  top: -5,    left: 'calc(50% - 5px)' }} onMouseDown={(e) => onResizeMouseDown(e, 'n')} />
                </div>
              );
            })()}
          </div>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Drag text fields on the poster to reposition
          </Text>
        </div>

        {/* ── Settings Panel ── */}
        <div style={{ flex: 1, minWidth: 260 }}>

          {/* Field List */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Text strong>Text Fields ({fields.length})</Text>
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addField}>Add Field</Button>
          </div>

          {fields.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>No fields yet. Click "Add Field" to start.</Text>
          )}

          {fields.map((f, i) => (
            <div
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '4px 8px', marginBottom: 4,
                border: `1px solid ${selectedId === f.id ? '#1890ff' : '#e8e8e8'}`,
                borderRadius: 4,
                background: selectedId === f.id ? '#e6f4ff' : '#fff',
                cursor: 'pointer',
              }}
            >
              <Text ellipsis style={{ maxWidth: 170, fontSize: 12 }}>
                <span style={{ color: '#aaa', marginRight: 4 }}>{i + 1}.</span>
                {f.template || '(empty)'}
              </Text>
              <Button size="small" type="text" danger icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); remove(f.id); }} />
            </div>
          ))}

          <Divider style={{ margin: '10px 0' }} />

          {/* Field Editor */}
          {sel ? (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8, color: '#1890ff' }}>
                Editing Field #{fields.findIndex(f => f.id === sel.id) + 1}
              </Text>

              {/* Template */}
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 12 }}>Template</Text>
                <Input.TextArea
                  rows={2}
                  value={sel.template}
                  onChange={e => update(selectedId, 'template', e.target.value)}
                  style={{ marginTop: 2 }}
                  placeholder="{name} {relation} {father_name}"
                />
              </div>

              {/* Token chips */}
              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11, color: '#888' }}>
                  <InfoCircleOutlined style={{ marginRight: 3 }} />
                  Click token to append to template:
                </Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {TOKENS.map(t => (
                    <Tag key={t.key} color="blue" style={{ cursor: 'pointer', fontSize: 11 }} onClick={() => insertToken(t.key)}>
                      {t.label}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* Font Family */}
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 12 }}>
                  <FontSizeOutlined style={{ marginRight: 4 }} />
                  Font Family
                </Text>
                <Select
                  value={sel.font_family || DEFAULT_FONT}
                  onChange={v => update(selectedId, 'font_family', v)}
                  style={{ width: '100%', marginTop: 2 }}
                  optionLabelProp="label"
                >
                  {FONT_FAMILIES.map(f => (
                    <Option key={f.value} value={f.value} label={f.label}>
                      <span style={{ fontFamily: `'${f.value}', Arial, sans-serif` }}>
                        {f.label} — <span style={{ color: '#888', fontSize: 11 }}>अ आ इ — Aa Bb</span>
                      </span>
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Font size + Color */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: 12 }}>Font Size (px)</Text>
                  <InputNumber size="small" min={10} max={300} value={sel.font_size}
                    onChange={v => update(selectedId, 'font_size', v)} style={{ width: '100%', marginTop: 2 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: 12 }}>Color</Text>
                  <input type="color" value={sel.color || '#000000'}
                    onChange={e => update(selectedId, 'color', e.target.value)}
                    style={{ width: '100%', height: 28, marginTop: 2, padding: 2, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }} />
                </div>
              </div>

              {/* Bold + Align */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 12 }}>Bold</Text>
                  <Switch size="small" checked={sel.bold} onChange={v => update(selectedId, 'bold', v)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 12 }}>Align</Text>
                  <Select size="small" value={sel.text_anchor || 'middle'} onChange={v => update(selectedId, 'text_anchor', v)} style={{ width: 90 }}>
                    <Option value="middle">Center</Option>
                    <Option value="start">Left</Option>
                    <Option value="end">Right</Option>
                  </Select>
                </div>
              </div>

              {/* Position controls */}
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 12 }}>Position (drag on poster or use arrows)</Text>
              </div>

              {/* Nudge arrows */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 8 }}>
                <Button size="small" icon={<ArrowUpOutlined />}
                  onClick={() => update(selectedId, 'top_pct', Math.max(0, (sel.top_pct || 0) - 0.5))} />
                <div style={{ display: 'flex', gap: 2 }}>
                  <Button size="small" icon={<ArrowLeftOutlined />}
                    onClick={() => update(selectedId, 'left_pct', Math.max(0, (sel.left_pct ?? 50) - 0.5))} />
                  <Button size="small" icon={<ArrowDownOutlined />}
                    onClick={() => update(selectedId, 'top_pct', Math.min(99, (sel.top_pct || 0) + 0.5))} />
                  <Button size="small" icon={<ArrowRightOutlined />}
                    onClick={() => update(selectedId, 'left_pct', Math.min(100, (sel.left_pct ?? 50) + 0.5))} />
                </div>
              </div>

              {/* Top % + Left % manual inputs */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: 12 }}>Top %</Text>
                  <InputNumber size="small" min={0} max={99} precision={1}
                    value={Math.round(sel.top_pct * 10) / 10}
                    onChange={v => update(selectedId, 'top_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: 12 }}>Left %</Text>
                  <InputNumber size="small" min={0} max={100} precision={1}
                    value={Math.round((sel.left_pct ?? 50) * 10) / 10}
                    onChange={v => update(selectedId, 'left_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                </div>
              </div>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click a field in the list or on the poster to edit it.
            </Text>
          )}

          <Divider style={{ margin: '10px 0' }} />

          {/* Photo Config */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>Member Photo Overlay</Text>
              <Switch size="small" checked={photo.enabled} onChange={v => setPhoto(p => ({ ...p, enabled: v }))} />
            </div>
            {photo.enabled && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { label: 'Top %',    key: 'top_pct' },
                  { label: 'Left %',   key: 'left_pct' },
                  { label: 'Width %',  key: 'width_pct' },
                  { label: 'Height %', key: 'height_pct' },
                ].map(({ label, key }) => (
                  <div key={key} style={{ flex: '1 1 70px' }}>
                    <Text style={{ fontSize: 11 }}>{label}</Text>
                    <InputNumber size="small" min={0} max={100} precision={1}
                      value={photo[key] ?? photo.size_pct ?? 15}
                      onChange={v => setPhoto(p => ({ ...p, [key]: v }))}
                      style={{ width: '100%', marginTop: 2 }} />
                  </div>
                ))}
                <div style={{ flex: '1 1 70px', display: 'flex', alignItems: 'center', gap: 4, paddingTop: 18 }}>
                  <Text style={{ fontSize: 11 }}>Circle</Text>
                  <Switch size="small" checked={photo.circle} onChange={v => setPhoto(p => ({ ...p, circle: v }))} />
                </div>
                {!photo.circle && (
                  <div style={{ flex: '1 1 70px' }}>
                    <Text style={{ fontSize: 11 }}>Corner Radius (px)</Text>
                    <InputNumber size="small" min={0} max={500} precision={0}
                      value={photo.border_radius ?? 0}
                      onChange={v => setPhoto(p => ({ ...p, border_radius: v }))}
                      style={{ width: '100%', marginTop: 2 }} />
                  </div>
                )}
                <div style={{ flex: '1 1 100%' }}>
                  <Text style={{ fontSize: 11 }}>Border</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <Switch size="small" checked={photo.border_enabled} onChange={v => setPhoto(p => ({ ...p, border_enabled: v }))} />
                    {photo.border_enabled && (
                      <>
                        <InputNumber size="small" min={1} max={50} value={photo.border_width}
                          onChange={v => setPhoto(p => ({ ...p, border_width: v }))} style={{ width: 60 }} />
                        <input type="color" value={photo.border_color || '#ffffff'}
                          onChange={e => setPhoto(p => ({ ...p, border_color: e.target.value }))}
                          style={{ width: 32, height: 24, padding: 1, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PosterDesigner;
